/**
 * LDB Duel — Custom Card Sleeve Designer
 * Procedural 3D sleeve preview + artwork placement tool.
 *
 * Architecture notes for future maintainers:
 *  - three.js is loaded as an ES module via dynamic import(). The URL is passed
 *    in on a data attribute because Liquid cannot be used inside .js assets.
 *  - The sleeve is generated in code, not loaded from a .glb. Finish styles are
 *    material parameters, so variant selection changes the render live.
 *  - The printed face is a separate flat mesh sitting just outside the opaque
 *    base slab. This matters: the base slab's inner surface must stay plain
 *    black/white, because you see it through the transparent front.
 *  - The product form is re-rendered by product-info.js on every variant change
 *    (it replaces the innerHTML of #ProductBuyButtons). Any input we inject is
 *    therefore destroyed on each variant switch and must be re-injected. See
 *    ensureFormInputs().
 */
(function () {
  'use strict';

  var MM_PER_INCH = 25.4;
  var PX_PER_MM = 8;            // wrap texture: 284.5mm -> 2276px wide
  var STITCH_INSET = 2;         // every seam sits 2mm inside the trimmed edge
  var LOW_DPI_THRESHOLD = 300;
  var FIT_ASPECT = 0.6575;      // stage width / height the 3D framing was set at
  function prefersReducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  var FLAP_OPEN = 0.62;         // radians each side flap swings out
  var FLIP_LIFT = 46;           // mm the cover lifts clear of the body
  var SEAT_PROUD = 0.35;        // cover seats this proud on the front, capping
                                // the body plates' cut edges (see applyMount)
  var FLIP_STEP = 1 / 110;      // ~1.8s at 60fps

  /** Flattens the die-cut path ops into a dense polyline, in millimetres. */
  function samplePath(ops, step) {
    var pts = [], cur = null;
    function push(x, y) { pts.push([x, y]); cur = [x, y]; }
    for (var i = 0; i < ops.length; i++) {
      var o = ops[i];
      if (o[0] === 'M' || o[0] === 'L') push(o[1], o[2]);
      else {
        var p0 = cur, cx = o[1], cy = o[2], x1 = o[3], y1 = o[4];
        for (var t = step; t <= 1.0001; t += step) {
          var mt = 1 - t;
          push(mt * mt * p0[0] + 2 * mt * t * cx + t * t * x1,
               mt * mt * p0[1] + 2 * mt * t * cy + t * t * y1);
        }
      }
    }
    return pts;
  }

  /**
   * Offsets a closed polyline inward by d, along the averaged normal at each
   * vertex. Used for the trim line and for the stitching that sits 2mm inside
   * it -- both have to follow the notches, which a plain inset rectangle does
   * not do.
   */
  function offsetPoly(pts, d) {
    var out = [], n = pts.length;
    for (var i = 0; i < n; i++) {
      var p = pts[i], a = pts[(i - 1 + n) % n], b = pts[(i + 1) % n];
      var n1x = p[1] - a[1], n1y = a[0] - p[0];
      var n2x = b[1] - p[1], n2y = p[0] - b[0];
      var l1 = Math.hypot(n1x, n1y) || 1, l2 = Math.hypot(n2x, n2y) || 1;
      var nx = n1x / l1 + n2x / l2, ny = n1y / l1 + n2y / l2;
      var l = Math.hypot(nx, ny) || 1;
      out.push([p[0] + nx / l * d, p[1] + ny / l * d]);
    }
    return out;
  }

  function tracePoly(ctx, pts, k, ox, oy) {
    ctx.beginPath();
    for (var i = 0; i < pts.length; i++) {
      var x = ox + pts[i][0] * k, y = oy + pts[i][1] * k;
      if (i) ctx.lineTo(x, y); else ctx.moveTo(x, y);
    }
    ctx.closePath();
  }

  /** The die-cut outline, cached -- it never changes. */
  function dieOutline() {
    if (!dieOutline._pts && typeof lidOutline === 'function') {
      dieOutline._pts = samplePath(lidOutline(LID_SPEC), 0.25);
    }
    return dieOutline._pts || [];
  }  // the print spec asks for 400+; warn below 300
  var STACK_BREAKPOINT = 1199;  // must match the media query in the CSS


  function boot() {
    var roots = document.querySelectorAll('[data-deckbox-designer]');
    for (var i = 0; i < roots.length; i++) {
      // shopify:section:load fires on EVERY setting change in the theme editor.
      // Without this guard each firing built another instance on the same root,
      // each with its own listeners and its own 3D renderer.
      if (roots[i].__dbxInit) continue;
      roots[i].__dbxInit = true;
      try {
        new DeckboxDesigner(roots[i]);
      } catch (err) {
        roots[i].__dbxInit = false;
        console.error('[deckbox-designer] init failed', err);
      }
    }
  }

  // ---------------------------------------------------------------- helpers

  function parseJSONAttr(el, name, fallback) {
    var raw = el.getAttribute(name);
    if (!raw) return fallback;
    try { return JSON.parse(raw); } catch (e) {
      console.warn('[deckbox-designer] bad JSON in ' + name, e);
      return fallback;
    }
  }

  function clamp(v, lo, hi) { return Math.min(hi, Math.max(lo, v)); }

  function hasWebGL() {
    try {
      var c = document.createElement('canvas');
      return !!(window.WebGLRenderingContext &&
        (c.getContext('webgl2') || c.getContext('webgl')));
    } catch (e) { return false; }
  }

  function roundedRectPath(ctx, x, y, w, h, r) {
    r = Math.min(r, w / 2, h / 2);
    // Square corners: draw an actual rectangle rather than degenerate curves.
    if (!(r > 0.5)) {
      ctx.beginPath();
      ctx.rect(x, y, w, h);
      ctx.closePath();
      return;
    }
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    ctx.lineTo(x + w, y + h - r);
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    ctx.lineTo(x + r, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
  }

  /** Filenames reach Shopify's file storage, so strip anything unsafe. */
  function slugify(str) {
    return String(str)
      .toLowerCase()
      .normalize ? String(str).toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
        .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40) || 'sleeve'
      : String(str).toLowerCase().replace(/[^a-z0-9]+/g, '-');
  }

  function bytesToLabel(b) {
    if (b < 1024) return b + ' B';
    if (b < 1024 * 1024) return Math.round(b / 1024) + ' KB';
    return (b / (1024 * 1024)).toFixed(1) + ' MB';
  }

  // Scale factor mapping image pixels -> canvas pixels such that the rotated
  // image fully covers the canvas.
  function coverFactor(iw, ih, cw, ch, rot) {
    var a = Math.abs(Math.cos(rot));
    var b = Math.abs(Math.sin(rot));
    var bboxW = iw * a + ih * b;
    var bboxH = iw * b + ih * a;
    return Math.max(cw / bboxW, ch / bboxH);
  }

  // ------------------------------------------------------------ constructor

  function DeckboxDesigner(root) {
    this.root = root;
    root.__dbx = this;
    // The Mach 3 wrap: one landscape print across three panels
    // (side | front | side), heights/bleed per the printer's spec sheet.
    this.wrapW = parseFloat(root.getAttribute('data-wrap-w'));      // mm
    if (isNaN(this.wrapW)) this.wrapW = 284.5;
    this.wrapH = parseFloat(root.getAttribute('data-wrap-h'));
    if (isNaN(this.wrapH)) this.wrapH = 152.4;
    this.panelSide = parseFloat(root.getAttribute('data-panel-side'));
    if (isNaN(this.panelSide)) this.panelSide = 87.5;
    this.panelFront = parseFloat(root.getAttribute('data-panel-front'));
    if (isNaN(this.panelFront)) this.panelFront = 109.5;
    this.bleedMM = parseFloat(root.getAttribute('data-bleed-mm'));
    if (isNaN(this.bleedMM)) this.bleedMM = 7.6;
    this.creaseMM = parseFloat(root.getAttribute('data-crease-mm'));
    if (isNaN(this.creaseMM)) this.creaseMM = 3.2;
    this.lidSeamMM = parseFloat(root.getAttribute('data-lid-seam-mm'));
    if (isNaN(this.lidSeamMM)) this.lidSeamMM = 45;   // from the box top

    this.maxBytes = (parseFloat(root.getAttribute('data-max-mb')) || 5) * 1024 * 1024;
    this.threeUrl = root.getAttribute('data-three-url');
    this.colorOptionName = root.getAttribute('data-color-option') || 'Color';
    this.requireArtwork = root.getAttribute('data-require-artwork') === 'true';

    this.qtyOptionName = root.getAttribute('data-qty-option') || '';
    this.shortenLabels = root.getAttribute('data-short-labels') !== 'false';
    this.hideOriginal = root.getAttribute('data-hide-original') === 'true';
    this.adoptBuy = root.getAttribute('data-adopt-buy') === 'true';
    this.collapseHeading = root.getAttribute('data-collapse-heading') || '';
    this.collapseOpen = root.getAttribute('data-collapse-open') === 'true';
    this.movePaymentTerms = root.getAttribute('data-move-terms') === 'true';

    // artwork colour boost, applied to the composited print texture
    this.artSat = parseFloat(root.getAttribute('data-art-sat'));
    if (isNaN(this.artSat)) this.artSat = 1.4;
    this.artBright = parseFloat(root.getAttribute('data-art-bright'));
    if (isNaN(this.artBright)) this.artBright = 1.0;
    this.moneySample = root.getAttribute('data-money-sample') || '$1.00';
    this.variants = parseJSONAttr(root, 'data-variants', null);
    if (!this.variants) {
      var vnode = root.querySelector('[data-dbx-variants-json]');
      if (vnode) { try { this.variants = JSON.parse(vnode.textContent); } catch (e) { this.variants = []; } }
    }
    this.variants = this.variants || [];

    this.state = {
      colorKey: 'Black',
      img: null,
      file: null,
      tx: 0,          // mm offset from wrap centre
      ty: 0,
      scale: 1,
      rot: 0
    };

    this.pointers = {};
    this.dragging = false;

    this.cacheDom();
    this.buildTextureCanvas();
    this.bindUI();
    this.syncFromVariantPicker();
    this.watchVariant();
    this.render2D();

    if (hasWebGL() && this.threeUrl) {
      this.init3D();
    } else if (!this.threeUrl) {
      this.fail3D('three.global.min.js is not in the theme assets folder (asset_url resolved to nothing).');
    } else {
      this.fail3D('WebGL is unavailable in this browser.');
    }

    // Core first, and each optional enhancement isolated. Previously a throw in
    // any one of these took out everything after it -- the pills and price
    // simply never rendered, with the tool looking half-built. Presentation
    // extras must never be able to break selection, pricing or the cart.
    var self = this;
    function step(name, fn) {
      try {
        fn.call(self);
      } catch (err) {
        console.error('[deckbox-designer] ' + name + ' failed (continuing)', err);
      }
    }

    step('syncTabs', this.syncTabs);
    step('hideOriginalPicker', this.hideOriginalPicker);
    step('adoptBuyButtons', this.adoptBuyButtons);

    // core: these must run even if anything above misbehaved
    step('buildOptionProxy', this.buildOptionProxy);
    step('updatePrice', this.updatePrice);
    step('ensureFormInputs', this.ensureFormInputs);
    step('wireAtc', this.wireAtc);
    step('updateGate', this.updateGate);
    step('bindBuyGuard', this.bindBuyGuard);
    step('alignPriceToButtons', this.alignPriceToButtons);
    var self2 = this;
    setTimeout(function () { self2.alignPriceToButtons(); }, 800);
    setTimeout(function () { self2.alignPriceToButtons(); }, 2500);

    // purely presentational, deliberately last
    step('collapseDescriptionSection', this.collapseDescriptionSection);
    step('adoptPaymentTerms', this.adoptPaymentTerms);
  }

  DeckboxDesigner.prototype.cacheDom = function () {
    var r = this.root;
    var q = function (sel) { return r.querySelector(sel); };
    this.el = {
      stage3d: q('[data-dbx-stage]'),
      view: q('[data-dbx-view]'),
      fileInput: q('[data-dbx-file]'),
      dropzone: q('[data-dbx-dropzone]'),
      zoom: q('[data-dbx-zoom]'),
      rotate: q('[data-dbx-rotate]'),
      reset: q('[data-dbx-reset]'),
      remove: q('[data-dbx-remove]'),
      status: q('[data-dbx-status]'),
      dpi: q('[data-dbx-dpi]'),
      filename: q('[data-dbx-filename]'),
      controls: q('[data-dbx-controls]'),
      tabs: r.querySelectorAll('[data-dbx-tab]'),
      panes: r.querySelectorAll('[data-dbx-pane]'),
      spinBtn: q('[data-dbx-spin]'),
      mountBtns: r.querySelectorAll('[data-dbx-mount]'),
      sizeLabel: q('[data-dbx-size-label]'),
      finishLabel: q('[data-dbx-finish-label]'),
      optionsPrimary: q('[data-dbx-options="primary"]'),
      optionsSecondary: q('[data-dbx-options="secondary"]'),
      priceNow: q('[data-dbx-price-now]'),
      priceWas: q('[data-dbx-price-was]'),
      priceUnit: q('[data-dbx-price-unit]'),
      atc: q('[data-dbx-atc]'),
      buySlot: q('[data-dbx-buy-slot]'),
      canvasWrap: q('.dbx-canvas-wrap')
    };
  };

  // ------------------------------------------------------------- 2D texture

  /** The whole printable wrap, in mm, plus derived panel x-positions. */
  DeckboxDesigner.prototype.size = function () {
    return { w: this.wrapW, h: this.wrapH };
  };

  DeckboxDesigner.prototype.creaseXs = function () {
    return [this.panelSide, this.panelSide + this.panelFront];
  };

  DeckboxDesigner.prototype.buildTextureCanvas = function () {
    this.texCanvas = document.createElement('canvas');
    this.texCtx = this.texCanvas.getContext('2d');
    // The 3D print renders from a filtered COPY, so the saturation/brightness
    // boost applies to the preview without altering the 2D editor, the
    // customer's file, or what the designer receives.
    this.fxCanvas = document.createElement('canvas');
    this.fxCtx = this.fxCanvas.getContext('2d');
  };

  DeckboxDesigner.prototype.updateFxCanvas = function () {
    var src = this.texCanvas, dst = this.fxCanvas, ctx = this.fxCtx;
    if (dst.width !== src.width) dst.width = src.width;
    if (dst.height !== src.height) dst.height = src.height;
    ctx.clearRect(0, 0, dst.width, dst.height);
    var f = 'saturate(' + this.artSat + ') brightness(' + this.artBright + ')';
    try { ctx.filter = f; } catch (e) { /* very old browsers: unfiltered copy */ }
    ctx.drawImage(src, 0, 0);
    ctx.filter = 'none';
  };

  DeckboxDesigner.prototype.isWhite = function () {
    return String(this.state.colorKey).toLowerCase().indexOf('white') !== -1;
  };

  /** Base the print sits on, drawn into the sheet texture. */
  DeckboxDesigner.prototype.baseColorHex = function () {
    return this.isWhite() ? '#f4f2ee' : '#1e1e22';
  };

  /** Bare leatherette on the compartments, top and floor. */
  DeckboxDesigner.prototype.panelColorHex = function () {
    return this.isWhite() ? '#f0ede7' : '#2b2b30';
  };

  /**
   * Draws the full wrap into this.texCanvas: leather-coloured base, then the
   * customer's artwork. Unlike the sleeves there is no printed border -- the
   * wrap goes edge to edge (the bleed is trimmed in production).
   */
  DeckboxDesigner.prototype.drawTexture = function () {
    var sz = this.size();
    var cw = Math.round(sz.w * PX_PER_MM);
    var ch = Math.round(sz.h * PX_PER_MM);
    if (this.texCanvas.width !== cw) this.texCanvas.width = cw;
    if (this.texCanvas.height !== ch) this.texCanvas.height = ch;

    var ctx = this.texCtx;
    ctx.clearRect(0, 0, cw, ch);
    ctx.fillStyle = this.baseColorHex();
    ctx.fillRect(0, 0, cw, ch);

    var st = this.state;
    if (st.img) {
      var iw = st.img.naturalWidth || st.img.width;
      var ih = st.img.naturalHeight || st.img.height;
      var k = coverFactor(iw, ih, cw, ch, st.rot) * st.scale;
      ctx.save();
      ctx.translate(cw / 2 + st.tx * PX_PER_MM, ch / 2 + st.ty * PX_PER_MM);
      ctx.rotate(st.rot);
      ctx.drawImage(st.img, -iw * k / 2, -ih * k / 2, iw * k, ih * k);
      ctx.restore();
      this.lastCover = k;
    } else {
      this.lastCover = null;
    }

    // No crease bands here. They belong on the editor canvas as a guide, but
    // not in the print texture: the crease is where the sheet folds, which is
    // the box's corner, so on the 3D model it would read as a dark stripe
    // painted down an edge the artwork actually wraps straight around.
    // Stitching does belong here -- it wraps onto the box with everything else.
    var poly = dieOutline();
    if (poly.length) {
      var white = this.isWhite();
      var stitch = offsetPoly(poly, this.bleedMM + STITCH_INSET);
      ctx.save();
      ctx.strokeStyle = white ? '#e4e0d8' : '#0a0a0b';
      // 3.63mm pitch at 33% duty with ~0.55mm thread, measured off LDB's
      // close-up photo -- roughly a 7-stitch-per-inch leather seam
      ctx.lineWidth = 0.55 * PX_PER_MM;
      ctx.lineCap = 'round';
      ctx.setLineDash([1.20 * PX_PER_MM, 2.43 * PX_PER_MM]);
      tracePoly(ctx, stitch, PX_PER_MM, 0, 0);
      ctx.stroke();
      ctx.restore();
    }

    if (this.tex) {
      this.updateFxCanvas();
      this.tex.needsUpdate = true;
      this.needsRender = true;
    }
  };

  /** Visible editor canvas: the texture, clipped to the sleeve outline, plus guides. */
  DeckboxDesigner.prototype.render2D = function () {
    this.drawTexture();

    var view = this.el.view;
    if (!view) return;
    var s = this.size();
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    var boxW = view.clientWidth || 320;
    var boxH = Math.round(boxW * (s.h / s.w));

    if (view.width !== Math.round(boxW * dpr) || view.height !== Math.round(boxH * dpr)) {
      view.width = Math.round(boxW * dpr);
      view.height = Math.round(boxH * dpr);
    }
    view.style.height = boxH + 'px';

    var ctx = view.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, boxW, boxH);

    var k = boxW / s.w;                 // canvas px per millimetre
    var poly = dieOutline();

    // The artwork is clipped to the die-cut, so the customer sees the actual
    // shape that gets cut -- notches included -- rather than a rectangle that
    // implies coverage they will not get.
    if (poly.length) {
      ctx.save();
      tracePoly(ctx, poly, k, 0, 0);
      ctx.clip();
      ctx.drawImage(this.texCanvas, 0, 0, boxW, boxH);
      ctx.restore();
    } else {
      ctx.drawImage(this.texCanvas, 0, 0, boxW, boxH);
    }

    if (!poly.length) { this.updateReadouts(); this.updateNote(); this.scheduleShot(); return; }

    ctx.save();
    ctx.lineWidth = 1;
    ctx.strokeStyle = 'rgba(255,255,255,0.55)';
    tracePoly(ctx, poly, k, 0, 0);
    ctx.stroke();

    // trim line: everything outside it is bleed and gets cut away
    ctx.setLineDash([5, 4]);
    ctx.strokeStyle = 'rgba(255,255,255,0.78)';
    tracePoly(ctx, offsetPoly(poly, this.bleedMM), k, 0, 0);
    ctx.stroke();
    ctx.setLineDash([]);

    // crease bands
    var creaseW = this.creaseMM * k;
    var xs = this.creaseXs();
    ctx.fillStyle = 'rgba(0,0,0,0.38)';
    for (var ci = 0; ci < xs.length; ci++) {
      ctx.fillRect(xs[ci] * k - creaseW / 2, 0, creaseW, boxH);
    }
    ctx.restore();

    this.updateReadouts();
    this.updateNote();
    this.scheduleShot();
  };

  /**
   * Placement snapshot for the order: the composited print sheet at full
   * print resolution (PX_PER_MM, screen-independent) with the die outline,
   * trim line and crease guides drawn over it — the same reference the
   * customer sees in the 2D editor. Attached as properties[Design Preview]
   * beside the original file; this is how the print team verifies the
   * customer's placement against the print-ready file. The cart thumbnail
   * patches prefer a "Design Preview" property, so cart lines pick it up too.
   *
   * Captured eagerly on a 600ms debounce after each change. If capture ever
   * fails the order still goes through with the artwork alone — a submit
   * path must never wait on an async artifact.
   */
  DeckboxDesigner.prototype.scheduleShot = function () {
    if (!this.state.img) return;
    var self = this;
    clearTimeout(this._shotTimer);
    this._shotTimer = setTimeout(function () { self.captureShot(); }, 600);
  };

  DeckboxDesigner.prototype.captureShot = function () {
    var self = this;
    if (!this.state.img || !this.texCanvas || !this.texCanvas.width) return;
    try {
      var c = document.createElement('canvas');
      if (!c.toBlob) return;
      var s = this.size();
      var boxW = this.texCanvas.width, boxH = this.texCanvas.height;
      c.width = boxW;
      c.height = boxH;
      var ctx = c.getContext('2d');
      var k = boxW / s.w;                 // canvas px per millimetre
      var lw = Math.max(2, boxW / 800);   // guide weight, visible at print scale
      var poly = dieOutline();

      if (poly.length) {
        ctx.save();
        tracePoly(ctx, poly, k, 0, 0);
        ctx.clip();
        ctx.drawImage(this.texCanvas, 0, 0);
        ctx.restore();

        ctx.save();
        ctx.lineWidth = lw;
        ctx.strokeStyle = 'rgba(255,255,255,0.55)';
        tracePoly(ctx, poly, k, 0, 0);
        ctx.stroke();

        ctx.setLineDash([5 * lw, 4 * lw]);
        ctx.strokeStyle = 'rgba(255,255,255,0.78)';
        tracePoly(ctx, offsetPoly(poly, this.bleedMM), k, 0, 0);
        ctx.stroke();
        ctx.setLineDash([]);

        var creaseW = this.creaseMM * k;
        var xs = this.creaseXs();
        ctx.fillStyle = 'rgba(0,0,0,0.38)';
        for (var ci = 0; ci < xs.length; ci++) {
          ctx.fillRect(xs[ci] * k - creaseW / 2, 0, creaseW, boxH);
        }
        ctx.restore();
      } else {
        ctx.drawImage(this.texCanvas, 0, 0);
      }

      c.toBlob(function (blob) {
        if (!blob || !self.state.img) return;
        self.shotFile = new File([blob], 'placement-preview.jpg', { type: 'image/jpeg' });
        var form = self.form || self.findForm();
        var input = form && form.querySelector('[data-dbx-prop="shot"]');
        if (input) self.setFileOnInput(input, self.shotFile);
      }, 'image/jpeg', 0.9);
    } catch (e) { /* best-effort only */ }
  };

  /** Keeps properties[_design] in step with the current state, every frame. */
  DeckboxDesigner.prototype.updateNote = function () {
    var form = this.form || this.findForm();
    if (!form) return;
    var note = form.querySelector('[data-dbx-prop="note"]');
    if (!note) return;
    note.value = this.state.file ? this.designNote() : '';
  };

  DeckboxDesigner.prototype.updateReadouts = function () {
    var s = this.size();
    if (this.el.sizeLabel) {
      this.el.sizeLabel.textContent = s.w / 10 + ' x ' + s.h / 10 + ' cm wrap';
    }
    if (this.el.finishLabel) {
      this.el.finishLabel.textContent = this.state.colorKey + ' faux leather';
    }
    if (this.el.dpi) {
      if (this.lastCover) {
        var ppi = (MM_PER_INCH * PX_PER_MM) / this.lastCover;
        var low = ppi < LOW_DPI_THRESHOLD;
        this.el.dpi.textContent = Math.round(ppi) + ' PPI at this size' +
          (low ? ' — aim for 400+ for a crisp print' : '');
        this.el.dpi.classList.toggle('dbx-warn', low);
        this.el.dpi.hidden = false;
      } else {
        this.el.dpi.hidden = true;
      }
    }
  };

  // ---------------------------------------------------------------- upload

  DeckboxDesigner.prototype.loadFile = function (file) {
    var self = this;
    if (!file) return;
    if (!/^image\//.test(file.type)) {
      return this.say('That file is not an image. Please upload a PNG or JPG.', true);
    }
    if (file.size > this.maxBytes) {
      return this.say('That file is ' + bytesToLabel(file.size) + '. Please keep it under ' +
        bytesToLabel(this.maxBytes) + '.', true);
    }

    var url = URL.createObjectURL(file);
    var img = new Image();
    img.onload = function () {
      // Downscale oversized images for the GPU texture. The customer's original
      // file is what goes to the cart, untouched.
      var MAXDIM = 2048;
      var big = Math.max(img.naturalWidth, img.naturalHeight);
      if (big > MAXDIM) {
        var r = MAXDIM / big;
        var c = document.createElement('canvas');
        c.width = Math.round(img.naturalWidth * r);
        c.height = Math.round(img.naturalHeight * r);
        c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
        var small = new Image();
        small.onload = function () { URL.revokeObjectURL(url); self.adopt(small, file); };
        small.src = c.toDataURL('image/png');
      } else {
        self.adopt(img, file);
      }
    };
    img.onerror = function () {
      URL.revokeObjectURL(url);
      self.say('That image could not be read. Try re-saving it as a PNG or JPG.', true);
    };
    img.src = url;
  };

  DeckboxDesigner.prototype.adopt = function (img, file) {
    this.state.img = img;
    this.state.file = file;
    this.state.tx = 0; this.state.ty = 0; this.state.scale = 1; this.state.rot = 0;
    if (this.el.zoom) this.el.zoom.value = '100';
    if (this.el.rotate) this.el.rotate.value = '0';
    if (this.el.filename) this.el.filename.textContent = file.name + ' — ' + bytesToLabel(file.size);
    this.root.classList.add('dbx--has-art');
    this.say('');

    // Auto-rotate kicks in once artwork exists, so the customer sees the
    // sleeve as an object without having to discover dragging. Any pointer
    // interaction with the 3D stops it (bindOrbit). Respects reduced-motion.
    if (this.renderer &&
        !(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches)) {
      this.spin = true;
      if (this.el.spinBtn) this.el.spinBtn.setAttribute('aria-pressed', 'true');
    }

    this.render2D();
    // (constructor runs the full step sequence; here only the form wiring
    // and gate need refreshing)
    this.ensureFormInputs();
    this.updateGate();
    this.updatePrice();
  };

  DeckboxDesigner.prototype.clearArt = function () {
    this.state.img = null;
    this.state.file = null;
    this.root.classList.remove('dbx--has-art');
    if (this.el.filename) this.el.filename.textContent = '';
    if (this.el.fileInput) this.el.fileInput.value = '';
    this.render2D();
    // (constructor runs the full step sequence; here only the form wiring
    // and gate need refreshing)
    this.ensureFormInputs();
    this.updateGate();
    this.updatePrice();
    this.resetColorToDefault();
  };

  /**
   * Removing the artwork returns the box to the black default (2026-09-18,
   * merchant request): a cleared tool should look like a freshly opened one,
   * not keep the colour chosen for the previous design. Goes through
   * chooseOption so the real variant, price and pills all follow, and matches
   * by keyword so renaming Black in admin (e.g. 'Black Leather') keeps working.
   */
  DeckboxDesigner.prototype.resetColorToDefault = function () {
    var groups = this.readRealOptions();
    for (var i = 0; i < groups.length; i++) {
      if (groups[i].name !== this.colorOptionName) continue;
      var target = null;
      for (var j = 0; j < groups[i].values.length; j++) {
        var v = groups[i].values[j];
        if (!v.disabled && String(v.value).toLowerCase().indexOf('black') !== -1) {
          target = v.value;
          break;
        }
      }
      if (target && groups[i].selected !== target) {
        this.chooseOption(this.colorOptionName, target);
      }
      return;
    }
  };

  DeckboxDesigner.prototype.say = function (msg, isError) {
    if (!this.el.status) return;
    this.el.status.textContent = msg || '';
    this.el.status.classList.toggle('dbx-warn', !!isError);
    this.el.status.hidden = !msg;
  };

  // ------------------------------------------------------------ interaction

  DeckboxDesigner.prototype.bindUI = function () {
    var self = this;
    var el = this.el;

    if (el.fileInput) {
      el.fileInput.addEventListener('change', function () {
        self.loadFile(this.files && this.files[0]);
      });
    }

    if (el.dropzone) {
      ['dragenter', 'dragover'].forEach(function (evt) {
        el.dropzone.addEventListener(evt, function (e) {
          e.preventDefault(); el.dropzone.classList.add('dbx-drop-hot');
        });
      });
      ['dragleave', 'drop'].forEach(function (evt) {
        el.dropzone.addEventListener(evt, function (e) {
          e.preventDefault(); el.dropzone.classList.remove('dbx-drop-hot');
        });
      });
      el.dropzone.addEventListener('drop', function (e) {
        if (e.dataTransfer && e.dataTransfer.files) self.loadFile(e.dataTransfer.files[0]);
      });
    }

    if (el.zoom) {
      el.zoom.addEventListener('input', function () {
        self.state.scale = parseFloat(this.value) / 100;
        self.render2D();
      });
    }
    if (el.rotate) {
      el.rotate.addEventListener('input', function () {
        self.state.rot = parseFloat(this.value) * Math.PI / 180;
        self.render2D();
      });
    }
    if (el.reset) {
      el.reset.addEventListener('click', function () {
        self.state.tx = 0; self.state.ty = 0; self.state.scale = 1; self.state.rot = 0;
        if (el.zoom) el.zoom.value = '100';
        if (el.rotate) el.rotate.value = '0';
        self.render2D();
      });
    }
    if (el.remove) {
      el.remove.addEventListener('click', function () { self.clearArt(); });
    }

    // drag to reposition on the 2D editor
    if (el.view) {
      var last = null;
      el.view.addEventListener('pointerdown', function (e) {
        if (!self.state.img) return;
        el.view.setPointerCapture(e.pointerId);
        self.pointers[e.pointerId] = { x: e.clientX, y: e.clientY };
        last = { x: e.clientX, y: e.clientY };
        self.dragging = true;
      });
      el.view.addEventListener('pointermove', function (e) {
        if (!self.dragging || !self.state.img) return;
        self.pointers[e.pointerId] = { x: e.clientX, y: e.clientY };
        var ids = Object.keys(self.pointers);
        if (ids.length >= 2) {
          // pinch zoom
          var a = self.pointers[ids[0]], b = self.pointers[ids[1]];
          var d = Math.hypot(a.x - b.x, a.y - b.y);
          if (self.pinchBase) {
            var next = clamp(self.pinchScale * (d / self.pinchBase), 0.25, 4);
            self.state.scale = next;
            if (el.zoom) el.zoom.value = String(Math.round(next * 100));
          } else {
            self.pinchBase = d; self.pinchScale = self.state.scale;
          }
          self.render2D();
          return;
        }
        var s = self.size();
        var mmPerPx = s.w / (el.view.clientWidth || 320);
        self.state.tx += (e.clientX - last.x) * mmPerPx;
        self.state.ty += (e.clientY - last.y) * mmPerPx;
        self.state.tx = clamp(self.state.tx, -s.w, s.w);
        self.state.ty = clamp(self.state.ty, -s.h, s.h);
        last = { x: e.clientX, y: e.clientY };
        self.render2D();
      });
      ['pointerup', 'pointercancel', 'pointerleave'].forEach(function (evt) {
        el.view.addEventListener(evt, function (e) {
          delete self.pointers[e.pointerId];
          if (!Object.keys(self.pointers).length) {
            self.dragging = false; self.pinchBase = null;
          }
        });
      });
      el.view.addEventListener('wheel', function (e) {
        if (!self.state.img) return;
        e.preventDefault();
        var next = clamp(self.state.scale * (e.deltaY < 0 ? 1.06 : 0.94), 0.25, 4);
        self.state.scale = next;
        if (el.zoom) el.zoom.value = String(Math.round(next * 100));
        self.render2D();
      }, { passive: false });
    }

    // tabs (mobile)
    for (var i = 0; i < el.tabs.length; i++) {
      (function (btn) {
        btn.addEventListener('click', function () {
          self.setTab(btn.getAttribute('data-dbx-tab'));
        });
      })(el.tabs[i]);
    }

    if (el.mountBtns && el.mountBtns.length) {
      Array.prototype.forEach.call(el.mountBtns, function (btn) {
        btn.addEventListener('click', function () {
          self.setMountSide(btn.getAttribute('data-dbx-mount') === 'front');
        });
      });
    }
    if (el.spinBtn) {
      el.spinBtn.addEventListener('click', function () { self.toggleSpin(); });
    }

    var ro = window.ResizeObserver ? new ResizeObserver(function () {
      self.syncTabs();
      self.render2D();
      self.resize3D();
    }) : null;
    if (ro) ro.observe(this.root);
    window.addEventListener('resize', function () { self.syncTabs(); self.alignPriceToButtons(); });
  };

  DeckboxDesigner.prototype.setTab = function (name) {
    var el = this.el, i;
    this.currentTab = name;
    for (i = 0; i < el.tabs.length; i++) {
      var t = el.tabs[i];
      var on = t.getAttribute('data-dbx-tab') === name;
      t.classList.toggle('dbx-tab--on', on);
      t.setAttribute('aria-selected', on ? 'true' : 'false');
    }
    for (i = 0; i < el.panes.length; i++) {
      var p = el.panes[i];
      p.hidden = p.getAttribute('data-dbx-pane') !== name;
    }
    if (name === 'preview') { this.resize3D(); }
    else { this.render2D(); }
  };

  /**
   * Keeps pane visibility in step with the CSS breakpoint.
   *
   * Below STACK_BREAKPOINT the panes stack and the tabs take over, so exactly
   * one pane must be visible. Without this the tabs rendered but nothing was
   * hidden until the customer clicked one -- both panes stacked and the section
   * ran ~1325px tall, defeating the no-scroll layout.
   */
  DeckboxDesigner.prototype.syncTabs = function () {
    var stacked = window.matchMedia
      ? window.matchMedia('(max-width: ' + STACK_BREAKPOINT + 'px)').matches
      : window.innerWidth <= STACK_BREAKPOINT;

    if (stacked) {
      if (!this.stacked) {
        this.stacked = true;
        this.setTab(this.currentTab || 'design');
      }
    } else if (this.stacked !== false) {
      this.stacked = false;
      for (var i = 0; i < this.el.panes.length; i++) this.el.panes[i].hidden = false;
      this.render2D();
      this.resize3D();
    }
  };

  // ------------------------------------------------------------------- 3D

  /**
   * Loads three.js as a CLASSIC script exposing window.THREE.
   *
   * This was a dynamic import() of the ESM build and it FAILED IN PRODUCTION
   * with "require is not defined" -- while importing fine from the console on
   * the same page. three.js itself never calls require(); the only matches in
   * the file are inside strings. Something else on the storefront (an app
   * bundler runtime -- LayoutHub, Afterpay and others are present) patches
   * dynamic import(). A plain <script> tag has none of that surface and cannot
   * be intercepted.
   *
   * The section also emits a deferred <script> for the global build, so
   * window.THREE is normally already there and this never has to fetch.
   */
  function loadThree(url, cb) {
    if (window.THREE) return cb(window.THREE);

    var existing = document.querySelector('script[data-dbx-three]');
    if (existing) {
      existing.addEventListener('load', function () { cb(window.THREE || null, 'script loaded but no global'); });
      existing.addEventListener('error', function () { cb(null, 'script tag failed'); });
      return;
    }
    if (!url) return cb(null, 'empty asset url');

    var sc = document.createElement('script');
    sc.src = url;
    sc.async = true;
    sc.setAttribute('data-dbx-three', '');
    sc.onload = function () { cb(window.THREE || null, window.THREE ? null : 'script loaded but no global'); };
    sc.onerror = function () {
      // last resort: some environments block injected scripts but allow modules
      try {
        import(url).then(function (m) { cb(m || null, null); })
                   .catch(function (e) { cb(null, 'script tag AND import failed: ' + (e && e.message)); });
      } catch (e) {
        cb(null, 'script tag failed and import unavailable');
      }
    };
    document.head.appendChild(sc);
  }

  DeckboxDesigner.prototype.init3D = function () {
    var self = this;
    loadThree(this.threeUrl, function (THREE, why) {
      if (!THREE) {
        return self.fail3D(
          'three.js did not load (' + (why || 'unknown') + '). Resolved URL: ' +
          (self.threeUrl || '(EMPTY - is three.global.min.js in the theme assets folder?)')
        );
      }
      self.THREE = THREE;
      try {
        self.build3D();
      } catch (err) {
        console.error('[deckbox-designer] 3D scene failed to build', err);
        self.fail3D('3D scene failed to build: ' + (err && err.message));
      }
    });
  };

  /** Visible, explained degradation -- never a silently missing feature. */
  DeckboxDesigner.prototype.fail3D = function (detail) {
    console.error('[deckbox-designer] ' + detail);
    this.root.classList.add('dbx--3d-off');
    var stage = this.el.stage3d;
    if (stage) {
      stage.textContent = '';
      var p = document.createElement('p');
      p.className = 'dbx-3d-error';
      p.textContent = '3D preview is not available in this browser. Your design ' +
        'and the placement above are still saved with your order.';
      stage.appendChild(p);
    }
    var spin = this.el.spinBtn;
    if (spin) spin.hidden = true;
  };

  DeckboxDesigner.prototype.build3D = function () {
    var THREE = this.THREE;
    var stage = this.el.stage3d;
    if (!stage) return;

    var renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    // ACES keeps the gloss highlights from blowing out, but it also darkens
    // and desaturates the artwork. Exposure is lifted here and the print
    // carries an emissiveMap (see buildSleeveMeshes) so the customer's
    // colours survive the lighting pipeline rather than being dimmed by it.
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.04;
    stage.appendChild(renderer.domElement);
    this.renderer = renderer;

    var scene = new THREE.Scene();
    this.scene = scene;

    // near/far are deliberately tight around the sleeve. The printed face sits
    // 0.0015mm off the film it decorates, and a 1..2000 depth range does not
    // have the precision to keep them apart.
    var camera = new THREE.PerspectiveCamera(28, 1, 80, 700);
    camera.position.set(0, 0, 330);
    this.camera = camera;

    // Procedural studio environment: a vertical gradient equirect, prefiltered.
    // Gives gloss and iridescence something real to reflect without shipping an HDR.
    var envCanvas = document.createElement('canvas');
    envCanvas.width = 32; envCanvas.height = 128;
    var ectx = envCanvas.getContext('2d');
    var grad = ectx.createLinearGradient(0, 0, 0, 128);
    grad.addColorStop(0.00, '#ffffff');
    grad.addColorStop(0.40, '#dbe3ee');
    grad.addColorStop(0.52, '#7f8798');
    grad.addColorStop(1.00, '#20242c');
    ectx.fillStyle = grad; ectx.fillRect(0, 0, 32, 128);
    var envTex = new THREE.CanvasTexture(envCanvas);
    envTex.mapping = THREE.EquirectangularReflectionMapping;
    var pmrem = new THREE.PMREMGenerator(renderer);
    scene.environment = pmrem.fromEquirectangular(envTex).texture;
    envTex.dispose();

    // A second environment just for gloss: a bright softbox band on a dark
    // studio, so glossy sleeves carry a visible light reflection that sweeps
    // across the surface as they rotate.
    var glossCanvas = document.createElement('canvas');
    glossCanvas.width = 256; glossCanvas.height = 128;
    var gctx = glossCanvas.getContext('2d');
    gctx.fillStyle = '#23262e';
    gctx.fillRect(0, 0, 256, 128);
    var band = gctx.createLinearGradient(0, 20, 0, 78);
    band.addColorStop(0, 'rgba(255,255,255,0)');
    band.addColorStop(0.5, 'rgba(255,255,255,0.95)');
    band.addColorStop(1, 'rgba(255,255,255,0)');
    gctx.fillStyle = band;
    gctx.save();
    gctx.translate(128, 48); gctx.rotate(-0.18);
    gctx.fillRect(-140, -30, 280, 60);
    gctx.restore();
    var glossTex = new THREE.CanvasTexture(glossCanvas);
    glossTex.mapping = THREE.EquirectangularReflectionMapping;
    this.envGloss = pmrem.fromEquirectangular(glossTex).texture;
    glossTex.dispose();
    pmrem.dispose();

    scene.add(new THREE.HemisphereLight(0xffffff, 0x404450, 0.55));
    var key = new THREE.DirectionalLight(0xffffff, 2.0);
    key.position.set(-120, 180, 240); scene.add(key);
    var fill = new THREE.DirectionalLight(0xdfe8ff, 0.7);
    fill.position.set(180, -60, 160); scene.add(fill);
    var rim = new THREE.DirectionalLight(0xffffff, 1.1);
    rim.position.set(60, 40, -220); scene.add(rim);

    this.group = new THREE.Group();
    scene.add(this.group);

    this.updateFxCanvas();
    this.tex = new THREE.CanvasTexture(this.fxCanvas);
    this.tex.colorSpace = THREE.SRGBColorSpace;
    this.tex.anisotropy = renderer.capabilities.getMaxAnisotropy();

    this.buildSleeveMeshes();
    this.applyFinishMaterial();

    // opening pose: printed back toward the viewer, slight tilt
    this.group.rotation.y = -0.26;
    this.group.rotation.x = 0.07;

    this.bindOrbit();
    this.resize3D();
    this.startLoop();
  };

  /**
   * The Mach 3 as one solid box. The wrap covers three vertical faces --
   * left side, front, right side -- as a single continuous texture (bleed
   * trimmed); back, top and bottom are plain leather. A recessed dark seam
   * runs around the box where the detachable lid sits.
   */
  DeckboxDesigner.prototype.buildSleeveMeshes = function () {
    var THREE = this.THREE, g = this.group;
    while (g.children.length) {
      var c = g.children.pop();
      g.remove(c);
    }
    if (typeof DeckBoxModel === 'undefined' || typeof LID_SPEC === 'undefined') {
      console.warn('[deckbox-designer] geometry modules missing');
      return;
    }
    var lid = LID_SPEC;
    lid.__profile = NOTCH_PROFILE;

    // Grain is generated once and shared. The leather figures are measured off
    // LDB's close-up: 0.41mm Voronoi cells at 0.105 contrast reproduce the
    // photo to within 2% on cell size and 1% on contrast.
    var TILE = 24;
    if (!this.grainLeather) {
      this.grainLeather = DeckBoxModel.grainMaps(THREE, { px: 512, tileMM: TILE, cellMM: 0.41, contrast: 0.105 });
      this.grainLinen = DeckBoxModel.grainMaps(THREE, { px: 512, tileMM: 12, cellMM: 0.16, contrast: 0.05 });
      // The cover's UVs span the printed sheet, so its tiling comes from the
      // sheet's real size; every other panel already carries mm-scaled UVs.
      this.coverNormal = this.grainLeather.normal.clone();
      this.coverRough = this.grainLeather.rough.clone();
      DeckBoxModel.tileFor(this.coverNormal, lid.w, lid.h, TILE);
      DeckBoxModel.tileFor(this.coverRough, lid.w, lid.h, TILE);
    }

    this.artMat = new THREE.MeshPhysicalMaterial({
      map: this.tex, normalMap: this.coverNormal, roughnessMap: this.coverRough,
      normalScale: new THREE.Vector2(1.0, 1.0),
      roughness: 0.58, metalness: 0, clearcoat: 0.42, clearcoatRoughness: 0.48
    });
    this.leatherMat = new THREE.MeshPhysicalMaterial({
      color: new THREE.Color(this.panelColorHex()),
      normalMap: this.grainLeather.normal, roughnessMap: this.grainLeather.rough,
      normalScale: new THREE.Vector2(1.0, 1.0),
      roughness: 0.58, metalness: 0, clearcoat: 0.42, clearcoatRoughness: 0.48
    });
    this.linenMat = new THREE.MeshPhysicalMaterial({
      color: new THREE.Color(0xcfcbc3), normalMap: this.grainLinen.normal,
      normalScale: new THREE.Vector2(0.45, 0.45), roughness: 0.97, metalness: 0
    });

    var self = this;
    this.model = DeckBoxModel.build(THREE, lid, {
      art: this.artMat, blackEdge: this.leatherMat,
      linen: this.linenMat, linenEdge: this.linenMat,
      stitchFor: function (w, h) { return self.stitchMaterial(w, h); }
    });
    g.add(this.model.group);
    this.boxDims = { W: this.model.box.w, D: this.model.box.d, H: this.model.box.h };

    // open/closed targets, eased every frame in tickAnim()
    this.anim = { drawer: [0, 0], drawerTarget: [0, 0] };
    // Mount-side flip. shellSpin is the resting rotation of the cover relative
    // to the body: 0 = mounted on the back, PI = mounted on the front.
    // A variant change rebuilds every mesh, so the chosen side is re-applied to
    // the fresh parts rather than reset -- switching Black to White must not
    // silently throw the customer back to the other view.
    if (!this.mount) {
      this.mount = { lidFront: false, shellSpin: 0, t: 0, dir: 0, running: false };
    }
    this.mount.t = 0; this.mount.dir = 0; this.mount.running = false;
    this.applyMount();
    this.syncMountButtons();
    this.needsRender = true;
  };

  /**
   * Thread for one sewn panel. Cached by panel size: the same rectangle is
   * asked for again on every rebuild (a variant change re-makes every mesh),
   * and re-rasterising it each time would be pure waste.
   */
  DeckboxDesigner.prototype.stitchMaterial = function (wMM, hMM) {
    var THREE = this.THREE;
    if (!THREE) return null;
    if (!this.stitchMats) this.stitchMats = {};
    var key = wMM.toFixed(2) + 'x' + hMM.toFixed(2);
    if (this.stitchMats[key]) return this.stitchMats[key];

    var cv = document.createElement('canvas');
    cv.width = Math.max(8, Math.round(wMM * PX_PER_MM));
    cv.height = Math.max(8, Math.round(hMM * PX_PER_MM));
    this.drawStitchCanvas(cv, wMM, hMM);

    var tex = new THREE.CanvasTexture(cv);
    tex.colorSpace = THREE.SRGBColorSpace;
    // No mipmaps. A seam is a 0.5mm line on a 100mm panel, so every mip level
    // averages it away against the transparent field around it and the
    // stitching fades out exactly when the box is small on screen.
    tex.generateMipmaps = false;
    tex.minFilter = THREE.LinearFilter;
    tex.magFilter = THREE.LinearFilter;
    // Thread sits ON the leather, so it must never write depth or occlude the
    // panel it decorates; the polygon offset keeps it off the face's plane.
    var mat = new THREE.MeshPhysicalMaterial({
      map: tex, transparent: true, depthWrite: false,
      polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2,
      roughness: 0.36, metalness: 0, clearcoat: 0.3, clearcoatRoughness: 0.4
    });
    mat.userData.stitchCanvas = cv;
    mat.userData.stitchMM = [wMM, hMM];
    this.stitchMats[key] = mat;
    return mat;
  };

  /**
   * One stitch line, 2mm inside the panel edge, matching the seam already
   * painted into the printed wrap: same 3.63mm pitch, same 0.55mm thread. The
   * groove pass underneath is what makes black-on-black read as stitching
   * rather than vanish -- on the real box it is the puckered leather either
   * side of the thread you actually see, not the thread's colour.
   */
  DeckboxDesigner.prototype.drawStitchCanvas = function (cv, wMM, hMM) {
    var ctx = cv.getContext('2d');
    var k = PX_PER_MM, white = this.isWhite();
    ctx.clearRect(0, 0, cv.width, cv.height);

    var inset = STITCH_INSET * k;
    var x = inset, y = inset;
    var w = cv.width - 2 * inset, h = cv.height - 2 * inset;
    if (w <= 2 || h <= 2) return;
    var r = Math.min(3.2 * k, w / 2, h / 2);

    function path() {
      ctx.beginPath();
      ctx.moveTo(x + r, y);
      ctx.arcTo(x + w, y, x + w, y + h, r);
      ctx.arcTo(x + w, y + h, x, y + h, r);
      ctx.arcTo(x, y + h, x, y, r);
      ctx.arcTo(x, y, x + w, y, r);
      ctx.closePath();
    }

    ctx.lineCap = 'round';
    ctx.setLineDash([1.20 * k, 2.43 * k]);

    // The pucker: a soft, wider, darker line the thread sits in. Kept narrow
    // and faint on white -- ivory thread on ivory leather is a whisper on the
    // real box, and a heavy groove turns it into a dark dashed border.
    ctx.strokeStyle = white ? 'rgba(140,133,120,0.38)' : 'rgba(0,0,0,0.62)';
    ctx.lineWidth = (white ? 0.95 : 1.15) * k;
    path(); ctx.stroke();

    // The thread itself, a shade off the leather either way, which is what
    // makes black-on-black legible at all: it is the sheen you see, not colour.
    ctx.strokeStyle = white ? '#fdfcf9' : '#5b5b66';
    ctx.lineWidth = 0.50 * k;
    path(); ctx.stroke();

    ctx.setLineDash([]);
  };

  /** Re-inks every cached seam after a colour change. */
  DeckboxDesigner.prototype.refreshStitching = function () {
    if (!this.stitchMats) return;
    var keys = Object.keys(this.stitchMats);
    for (var i = 0; i < keys.length; i++) {
      var m = this.stitchMats[keys[i]], d = m.userData.stitchMM;
      this.drawStitchCanvas(m.userData.stitchCanvas, d[0], d[1]);
      if (m.map) m.map.needsUpdate = true;
    }
  };
  /** Colour variant drives the leather; the printed wrap stays glossy. */
  DeckboxDesigner.prototype.applyFinishMaterial = function () {
    if (!this.leatherMat) return;
    this.leatherMat.color.set(this.panelColorHex());
    this.leatherMat.needsUpdate = true;
    this.refreshStitching();
    // the thread matches the base, so the printed sheet has to be redrawn too
    this.drawTexture();
    this.needsRender = true;
  };

  DeckboxDesigner.prototype.bindOrbit = function () {
    var self = this;
    var dom = this.renderer.domElement;
    var drag = false, last = null;
    dom.style.touchAction = 'none';

    var downAt = null;
    dom.addEventListener('pointerdown', function (e) {
      drag = true; last = { x: e.clientX, y: e.clientY };
      downAt = { x: e.clientX, y: e.clientY };
      self.spin = false;
      dom.setPointerCapture(e.pointerId);
    });
    // A click that did not travel is a click, not the end of a drag -- without
    // the distance test every orbit would also toggle whatever it finished on.
    dom.addEventListener('pointerup', function (e) {
      if (!downAt) return;
      var moved = Math.hypot(e.clientX - downAt.x, e.clientY - downAt.y);
      downAt = null;
      if (moved > 6) return;
      self.pickAndToggle(e);
    });
    dom.addEventListener('pointermove', function (e) {
      if (!drag) return;
      var dx = e.clientX - last.x, dy = e.clientY - last.y;
      self.group.rotation.y += dx * 0.008;
      self.group.rotation.x = clamp(self.group.rotation.x + dy * 0.006, -0.9, 0.9);
      last = { x: e.clientX, y: e.clientY };
      self.needsRender = true;
    });
    ['pointerup', 'pointercancel', 'pointerleave'].forEach(function (evt) {
      dom.addEventListener(evt, function () { drag = false; });
    });
  };

  /**
   * Works out which compartment was clicked and slides it forward; clicking
   * again puts it back. The top is fixed, so nothing else responds.
   */
  DeckboxDesigner.prototype.pickAndToggle = function (e) {
    var THREE = this.THREE;
    if (!THREE || !this.model || !this.renderer) return;
    var rect = this.renderer.domElement.getBoundingClientRect();
    var ndc = new THREE.Vector2(
      ((e.clientX - rect.left) / rect.width) * 2 - 1,
      -((e.clientY - rect.top) / rect.height) * 2 + 1
    );
    if (!this.raycaster) this.raycaster = new THREE.Raycaster();
    this.raycaster.setFromCamera(ndc, this.camera);
    var hits = this.raycaster.intersectObject(this.model.group, true);
    if (!hits.length) return;

    // The top is fixed on the real box, so only the two compartments are
    // pickable -- clicking the shell or the lid does nothing, as it should.
    // With the lid mounted on the front they are behind it, so nothing is
    // pickable at all rather than sliding a drawer through the cover.
    if (this.mount && (this.mount.lidFront || this.mount.running)) return;
    var drawers = this.model.parts.drawers;
    var obj = hits[0].object;
    while (obj) {
      for (var i = 0; i < drawers.length; i++) {
        if (drawers[i] === obj) {
          var open = this.anim.drawerTarget[i] > 1;
          // Only one compartment is out at a time, as on the real box: opening
          // one sends the other home rather than leaving both hanging out.
          for (var k = 0; k < drawers.length; k++) this.anim.drawerTarget[k] = 0;
          if (!open) this.anim.drawerTarget[i] = this.model.box.d * 0.62;
          this.needsRender = true;
          return;
        }
      }
      obj = obj.parent;
    }
  };

  /**
   * Flips which face the cover is magnet-mounted to.
   *
   * The choreography is the real gesture, in five beats: the side flaps swing
   * open on their fold lines, the cover lifts clear of the body, the body spins
   * 180 so its drawer face now points at the hovering cover, the cover drops
   * back on and the flaps wrap closed, then the whole assembly turns 180 to
   * present the newly covered face to the camera.
   *
   * Body and assembly each turn by PI, which nets to a full turn for the body
   * and a half turn for the cover -- exactly "the same lid, mounted the other
   * way round". At rest that is stored as shellSpin so the numbers never drift.
   */
  DeckboxDesigner.prototype.setMountSide = function (front, immediate) {
    if (!this.model) return;
    var m = this.mount;
    if (m.lidFront === front && !m.running) { this.syncMountButtons(); return; }
    if (m.running) return;                       // ignore clicks mid-flip

    // Freeze the view. Toggling the mount side is a deliberate comparison of two
    // arrangements, and the auto-rotate would keep turning the box through the
    // ~1.8s flip -- so the second arrangement arrives at a different angle from
    // the one it is being compared with, which reads as the toggle rotating it.
    this.stopSpin();

    // A compartment left hanging out would swing through the cover.
    this.anim.drawerTarget[0] = 0;
    this.anim.drawerTarget[1] = 0;

    if (immediate || prefersReducedMotion()) {
      m.lidFront = front;
      m.shellSpin = front ? Math.PI : 0;
      m.t = 0; m.dir = 0; m.running = false;
      this.applyMount();
      this.syncMountButtons();
      this.needsRender = true;
      return;
    }
    m.dir = front ? 1 : -1;
    m.t = 0;
    m.running = true;
    m.lidFront = front;
    this.syncMountButtons();
    this.needsRender = true;
  };

  /** Places every part for the current flip progress. */
  DeckboxDesigner.prototype.applyMount = function () {
    if (!this.model) return;
    var P = this.model.parts, m = this.mount;
    var t = m.running ? m.t : 1;
    var dir = m.dir;

    function seg(a, b) {
      var u = (m.t - a) / (b - a);
      u = u < 0 ? 0 : (u > 1 ? 1 : u);
      return u * u * (3 - 2 * u);               // smoothstep
    }

    var flap = 0, out = 0, bodyRot = 0, modelRot = 0;
    if (m.running) {
      flap = FLAP_OPEN * (seg(0.00, 0.20) - seg(0.66, 0.86));
      out = FLIP_LIFT * (seg(0.20, 0.42) - seg(0.66, 0.86));
      bodyRot = dir * Math.PI * seg(0.42, 0.66);
      modelRot = dir * Math.PI * seg(0.86, 1.00);
    }

    // The cover seats flush: it IS the wall on whichever three faces it covers,
    // and standing it proud would leave a strip of the box's sides uncovered at
    // the far end, which is the flaps appearing to overhang.
    var outSign = Math.cos(m.shellSpin) > 0.5 ? -1 : 1;
    P.shell.rotation.y = m.shellSpin;
    // Seated on the BACK the cover is flush: the body's plates stop a wall
    // short of that face, so nothing shares its plane. Seated on the FRONT it
    // stands a cap's thickness proud instead -- the top and floor plates run
    // out to the very plane its skin would occupy, and two coplanar faces show
    // through each other at grazing angles as pale lines along the top and
    // bottom edges. Same cure as the back fill, from the other side. 0.35mm is
    // below anything the eye resolves at this scale.
    var seat = Math.cos(m.shellSpin) < 0 ? SEAT_PROUD : 0;
    P.shell.position.z = outSign * out + seat;

    // The body has no back of its own while the cover is seated there. The
    // moment the cover leaves -- mid-flip or mounted on the front -- that face
    // has to be filled in, or you see straight through to the backs of the
    // compartments and the pale edge of the shelf between them.
    if (P.backFill) {
      var showBack = m.lidFront || m.running;
      P.backFill.visible = showBack;
      if (P.backFill.userData.liner) P.backFill.userData.liner.visible = showBack;
    }
    if (P.flaps) {
      P.flaps.left.rotation.y = -flap;
      P.flaps.right.rotation.y = flap;
    }
    P.body.rotation.y = bodyRot;
    this.model.group.rotation.y = modelRot;
  };

  /** Advances the flip. Returns true while it is moving. */
  DeckboxDesigner.prototype.tickMount = function () {
    var m = this.mount;
    if (!m.running) return false;
    m.t += FLIP_STEP;
    if (m.t >= 1) {
      // Fold the two half-turns into shellSpin and reset the rest, so repeated
      // flips cannot accumulate rotation error.
      m.shellSpin = m.lidFront ? Math.PI : 0;
      m.t = 0; m.dir = 0; m.running = false;
      this.applyMount();
        return true;
    }
    this.applyMount();
    return true;
  };

  DeckboxDesigner.prototype.syncMountButtons = function () {
    var btns = this.el.mountBtns;
    if (!btns) return;
    var front = this.mount.lidFront;
    Array.prototype.forEach.call(btns, function (b) {
      var isFront = b.getAttribute('data-dbx-mount') === 'front';
      var on = isFront === front;
      b.classList.toggle('dbx-mount--on', on);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
  };

  /** The hint has to stop promising a click that does nothing. */
  /** Eases the open/closed state one frame. Returns true while it is moving. */
  DeckboxDesigner.prototype.tickAnim = function () {
    if (!this.model || !this.anim) return false;
    var a = this.anim, moving = false, ease = 0.18, parts = this.model.parts;
    for (var i = 0; i < 2; i++) {
      var d = a.drawerTarget[i] - a.drawer[i];
      if (Math.abs(d) > 0.05) { a.drawer[i] += d * ease; moving = true; }
      else if (a.drawer[i] !== a.drawerTarget[i]) { a.drawer[i] = a.drawerTarget[i]; moving = true; }
      else continue;
      var home = i ? this.model.homeZ.upper : this.model.homeZ.lower;
      parts.drawers[i].position.z = home + a.drawer[i];
    }
    return moving;
  };

  /** Parks the auto-rotate wherever it is, button state included. */
  DeckboxDesigner.prototype.stopSpin = function () {
    if (!this.spin) return;
    this.spin = false;
    if (this.el.spinBtn) this.el.spinBtn.setAttribute('aria-pressed', 'false');
  };

  DeckboxDesigner.prototype.toggleSpin = function () {
    this.spin = !this.spin;
    if (this.el.spinBtn) {
      this.el.spinBtn.setAttribute('aria-pressed', this.spin ? 'true' : 'false');
    }
  };

  DeckboxDesigner.prototype.resize3D = function () {
    if (!this.renderer || !this.el.stage3d) return;
    var w = this.el.stage3d.clientWidth || 320;
    var h = this.el.stage3d.clientHeight || Math.round(w * 1.05);
    if (!w || !h) return;
    this.renderer.setSize(w, h, false);
    var aspect = w / h;
    this.camera.aspect = aspect;
    // Fit to whichever axis is tighter. The box turns about Y, so its widest
    // silhouette is the footprint diagonal, and a drawer pulled out adds to it.
    var d = this.boxDims || { W: 109.5, D: 87.5, H: 157.5 };
    var diag = Math.sqrt(d.W * d.W + d.D * d.D);
    // Framing is pinned to the aspect the layout was tuned at. Widening the
    // stage past it must not magnify the box -- the extra width becomes margin
    // around the model instead. A narrower stage still shrinks it to fit.
    var fitAspect = Math.min(aspect, FIT_ASPECT);
    var need = Math.max(d.H, diag / fitAspect) * 1.30;
    var z = need / (2 * Math.tan(this.camera.fov * Math.PI / 360));
    this.camera.position.z = z;
    // Hug the model with the depth range. Left at 10..2000 almost all of the
    // depth buffer's precision goes on empty space, and the butt-jointed walls
    // start to stipple against each other as the box turns.
    var radius = Math.sqrt(d.W * d.W + d.D * d.D + d.H * d.H) / 2;
    this.camera.near = Math.max(1, z - radius * 1.8);
    this.camera.far = z + radius * 2.4;
    this.camera.updateProjectionMatrix();
    this.needsRender = true;
  };

  DeckboxDesigner.prototype.startLoop = function () {
    var self = this;
    this.needsRender = true;
    function frame() {
      if (self.spin) {
        self.group.rotation.y += 0.006;
        self.needsRender = true;
      }
      if (self.tickAnim()) self.needsRender = true;
      if (self.tickMount()) self.needsRender = true;
      if (self.needsRender) {
        self.renderer.render(self.scene, self.camera);
        self.needsRender = false;
      }
      self.raf = requestAnimationFrame(frame);
    }
    frame();
  };

  // ------------------------------------------------------- variant plumbing

  DeckboxDesigner.prototype.findForm = function () {
    // Related products and quick-add drawers render their own /cart/add forms,
    // so an unscoped querySelector can grab the wrong one. Prefer a form inside
    // the main product wrapper.
    // Two homes are trusted: the main product wrapper, and THIS TOOL — after
    // adoptBuyButtons the real form lives inside the buy slot, at which point
    // the main-product preference no longer matches and plain document order
    // could hand back a quick-view form that renders earlier in the DOM.
    var forms = document.querySelectorAll('form[action*="/cart/add"]');
    var fallback = null;
    for (var i = 0; i < forms.length; i++) {
      var f = forms[i];
      if (!f.querySelector('[name="id"]')) continue;
      if (!fallback) fallback = f;
      if (f.closest('.main-product-view, #MainProduct, [data-section-type="main-product"]')) {
        return f;
      }
      if (this.root && this.root.contains(f)) return f;
    }
    return fallback;
  };

  /**
   * Finds the REAL controls for one product option.
   *
   * Metro names them differently depending on picker type, which is a trap:
   *   dropdown / select picker -> name="options[Card Size]"
   *   button / swatch picker   -> name="Card Size"        <- no options[] wrapper
   * Only checking for the first form meant reading nothing at all on a product
   * using pill swatches, silently falling back to each option's first value.
   */
  /**
   * The element option controls must live inside for Shopify to honour them.
   *
   * A page can hold several sets of identically-named option radios: the main
   * product, a quick-view drawer, a featured-product section. Querying the whole
   * document produced duplicate pills -- and worse, proxying a click to a radio
   * outside <product-info> means product-info.js discards it and the pill does
   * nothing at all.
   */
  DeckboxDesigner.prototype.productScope = function () {
    var form = this.findForm();
    var scope = form && form.closest
      ? form.closest('product-info, .main-product-view, #MainProduct')
      : null;
    if (!scope) {
      scope = document.querySelector('product-info') ||
              document.querySelector('.main-product-view, #MainProduct');
    }
    return scope || document;
  };

  DeckboxDesigner.prototype.realControlsFor = function (name) {
    var esc = String(name).replace(/"/g, '\\"');
    var sel = 'input[type="radio"][name="' + esc + '"],' +
              'input[type="radio"][name="options[' + esc + ']"],' +
              'select[name="options[' + esc + ']"],' +
              'select[name="' + esc + '"]';
    return this.productScope().querySelectorAll(sel);
  };

  /** Current option values + availability, read from the real controls. */
  DeckboxDesigner.prototype.readRealOptions = function () {
    var names = this.optionNames || [];
    var out = [];
    for (var i = 0; i < names.length; i++) {
      var nodes = this.realControlsFor(names[i]);
      if (!nodes.length) continue;
      var group = { name: names[i], values: [], selected: null };
      var seen = {};                      // one entry per distinct value
      function add(value, checked, disabled) {
        var key = '@' + value;
        if (seen[key]) {
          // merge: selected anywhere wins, unavailable everywhere to disable
          if (checked) { seen[key].checked = true; group.selected = value; }
          if (!disabled) seen[key].disabled = false;
          return;
        }
        seen[key] = { value: value, checked: !!checked, disabled: !!disabled };
        group.values.push(seen[key]);
        if (checked) group.selected = value;
      }
      for (var j = 0; j < nodes.length; j++) {
        var n = nodes[j];
        if (n.tagName === 'SELECT') {
          for (var k = 0; k < n.options.length; k++) {
            var o = n.options[k];
            add(o.value, o.selected, o.disabled);
          }
        } else {
          var dis = n.disabled || n.classList.contains('variant-input-disabled') ||
                    (n.parentNode && n.parentNode.classList &&
                     n.parentNode.classList.contains('variant-disabled'));
          add(n.value, n.checked, dis);
        }
      }
      out.push(group);
    }
    return out;
  };

  DeckboxDesigner.prototype.syncFromVariantPicker = function () {
    var groups = this.readRealOptions();
    var vals = {};
    for (var i = 0; i < groups.length; i++) {
      if (groups[i].selected != null) vals[groups[i].name] = groups[i].selected;
    }
    this.applyOptionValues(vals);
    return vals;
  };

  DeckboxDesigner.prototype.applyOptionValues = function (vals) {
    var changed = false;
    var colorVal = vals[this.colorOptionName];

    if (colorVal && colorVal !== this.state.colorKey) {
      this.state.colorKey = colorVal; changed = true;
    }
    if (changed) {
      if (this.group) { this.buildSleeveMeshes(); this.applyFinishMaterial(); this.resize3D(); }
      this.render2D();
    }
    return changed;
  };

  DeckboxDesigner.prototype.applyVariant = function (variant) {
    if (!variant || !variant.options) return;
    var vals = {};
    // variant.options is positional; map via the picker's option names
    var names = this.optionNames || [];
    for (var i = 0; i < variant.options.length; i++) {
      if (names[i]) vals[names[i]] = variant.options[i];
    }
    if (!Object.keys(vals).length) return this.syncFromVariantPicker();
    this.applyOptionValues(vals);
  };

  DeckboxDesigner.prototype.watchVariant = function () {
    var self = this;
    this.optionNames = parseJSONAttr(this.root, 'data-option-names', []);

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      try {
        subscribe(PUB_SUB_EVENTS.variantChange, function (evt) {
          var v = evt && evt.data && evt.data.variant;
          self.applyVariant(v);
          // the form is re-rendered on variant change; re-inject and re-gate
          setTimeout(function () {
            self.ensureFormInputs(); self.wireAtc(); self.updateGate();
            self.hideOriginalPicker(); self.adoptBuyButtons(); self.adoptPaymentTerms();
            self.alignPriceToButtons();
            self.refreshOptionProxy(); self.updatePrice();
          }, 0);
        });
      } catch (e) { /* fall through to DOM listeners */ }
    }

    document.addEventListener('variant:change', function (e) {
      self.applyVariant(e.detail && e.detail.variant);
      setTimeout(function () {
        self.ensureFormInputs(); self.wireAtc(); self.updateGate();
        self.refreshOptionProxy(); self.updatePrice();
      }, 0);
    });

    // belt and braces: any change in the picker
    document.addEventListener('change', function (e) {
      var t = e.target;
      if (t && t.name && t.name.indexOf('options[') === 0) {
        self.syncFromVariantPicker();
        setTimeout(function () {
          self.ensureFormInputs(); self.wireAtc(); self.updateGate();
          self.refreshOptionProxy(); self.updatePrice();
        }, 60);
      }
    });
  };


  // ------------------------------------------------- option proxy + pricing

  /**
   * Shortens an option value for a pill label.
   *
   * Only safe because these pills are a PROXY: the real radio inside
   * <product-info> keeps the full value, so cart and order data are untouched.
   * "Standard (Pokemon / MTG / One Piece / Riftbound)" as a pill label is what
   * forced the layout to ~2100px tall.
   */
  DeckboxDesigner.prototype.shortLabel = function (value) {
    if (!this.shortenLabels) return value;
    var v = String(value);
    v = v.replace(/\s*\([^)]*\)/g, '');          // drop parenthetical lists
    v = v.replace(/\s*[-–]\s*Borderless/i, ''); // "Matte - Borderless" -> "Matte"
    v = v.replace(/\bHolographic\b/i, 'Holo');
    v = v.replace(/\s+with\s+/i, ' / ');
    v = v.replace(/\s*Border\b/i, '');
    v = v.replace(/\s*Sleeves?\b/i, '');         // "25 Sleeves" -> "25"
    v = v.replace(/\s{2,}/g, ' ').trim();
    return v || String(value);
  };

  DeckboxDesigner.prototype.shortOptionName = function (name) {
    if (!this.shortenLabels) return name;
    if (/number of sleeves|quantity|qty/i.test(name)) return 'Quantity';
    return String(name).replace(/\s*style\b/i, '').replace(/\s*size\b/i, ' size').trim();
  };

  /**
   * Renders our own pills for every product option, split across two hosts:
   *   primary   -> beside the upload window, under the positioning controls
   *                (card size, finish -- the choices that change the preview)
   *   secondary -> full width below (quantity -- the choice that changes price)
   */
  DeckboxDesigner.prototype.buildOptionProxy = function () {
    var primary = this.el.optionsPrimary;
    var secondary = this.el.optionsSecondary;
    if (!primary && !secondary) return;

    var groups = this.readRealOptions();
    if (primary) { primary.textContent = ''; primary.hidden = true; }
    if (secondary) { secondary.textContent = ''; secondary.hidden = true; }
    if (!groups.length) return;

    var self = this;
    groups.forEach(function (g) {
      var isQty = self.isQtyOption(g.name);
      var host = (isQty ? secondary : primary) || secondary || primary;
      if (!host) return;

      var row = document.createElement('div');
      row.className = 'dbx-opt';
      row.setAttribute('data-dbx-opt-row', g.name);

      var lab = document.createElement('span');
      lab.className = 'dbx-opt-label';
      lab.textContent = self.shortOptionName(g.name);
      row.appendChild(lab);

      var ul = document.createElement('ul');
      ul.className = 'swatches-list' + (isQty ? ' dbx-qty' : '');

      g.values.forEach(function (v) {
        var li = document.createElement('li');
        li.className = 'swatches-list-item' +
          (v.checked ? ' active' : '') + (v.disabled ? ' variant-disabled' : '');
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'swatch-label';
        btn.textContent = self.shortLabel(v.value);
        btn.title = v.value;
        btn.setAttribute('data-dbx-opt', g.name);
        btn.setAttribute('data-dbx-val', v.value);
        if (v.disabled) btn.setAttribute('aria-disabled', 'true');
        li.appendChild(btn);
        ul.appendChild(li);
      });

      row.appendChild(ul);
      host.appendChild(row);
      host.hidden = false;
    });

    // one delegated listener on the root covers both hosts
    if (!this.root.__dbxOptBound) {
      this.root.__dbxOptBound = true;
      this.root.addEventListener('click', function (e) {
        var btn = e.target.closest ? e.target.closest('[data-dbx-val]') : null;
        if (!btn || btn.getAttribute('aria-disabled') === 'true') return;
        self.chooseOption(btn.getAttribute('data-dbx-opt'), btn.getAttribute('data-dbx-val'));
      });
    }
  };

  DeckboxDesigner.prototype.isQtyOption = function (name) {
    if (this.qtyOptionName && name === this.qtyOptionName) return true;
    return /number of sleeves|quantity|qty|sleeves/i.test(name);
  };

  /**
   * Forwards a pill click to the real control.
   *
   * This is the whole reason for the proxy. product-info.js does
   * `if (!this.contains(event.target)) return;` -- it ignores any option change
   * whose event target is outside <product-info>. Moving the picker into this
   * section would render perfectly and do nothing. Clicking the real input
   * keeps the event target where Metro expects it.
   */
  DeckboxDesigner.prototype.chooseOption = function (name, value) {
    var nodes = this.realControlsFor(name);
    for (var i = 0; i < nodes.length; i++) {
      var n = nodes[i];
      if (n.tagName === 'SELECT') {
        if (n.value === value) return;
        n.value = value;
        n.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }
      if (n.value === value) {
        if (n.checked) return;
        n.click();                                  // fires change from the real input
        return;
      }
    }
  };

  /** Mirrors selected/disabled state back onto the pills. */
  DeckboxDesigner.prototype.refreshOptionProxy = function () {
    var root = this.root;
    var groups = this.readRealOptions();
    groups.forEach(function (g) {
      var row = root.querySelector('[data-dbx-opt-row="' + String(g.name).replace(/"/g, '\\"') + '"]');
      if (!row) return;
      g.values.forEach(function (v) {
        var btn = row.querySelector('[data-dbx-val="' + String(v.value).replace(/"/g, '\\"') + '"]');
        if (!btn) return;
        var li = btn.parentNode;
        li.classList.toggle('active', !!v.checked);
        li.classList.toggle('variant-disabled', !!v.disabled);
        if (v.disabled) btn.setAttribute('aria-disabled', 'true');
        else btn.removeAttribute('aria-disabled');
      });
    });
  };

  /** Hides Metro's own picker so the page shows one set of options, not two. */
  /**
   * Belt-and-braces to the CSS rule the section emits. Scoped to the product's
   * own <product-info>: an unscoped document.querySelector('variant-selects')
   * grabs whichever comes first in the DOM, which may be a quick-view drawer's
   * picker rather than this product's -- hiding the wrong one and leaving the
   * real one on screen.
   *
   * Visually hidden only. These inputs must stay in the DOM, and inside
   * <product-info>, or variant selection breaks.
   */
  DeckboxDesigner.prototype.hideOriginalPicker = function () {
    if (!this.hideOriginal) return;
    var scope = this.productScope();
    if (!scope || !scope.querySelectorAll) return;
    var nodes = scope.querySelectorAll('variant-selects, .product-block-variant-picker');
    for (var i = 0; i < nodes.length; i++) {
      if (!nodes[i].classList.contains('dbx-visually-hidden')) {
        nodes[i].classList.add('dbx-visually-hidden');
      }
    }
  };

  // ---- pricing -----------------------------------------------------------

  DeckboxDesigner.prototype.moneyParts = function () {
    if (this._money) return this._money;
    var m = String(this.moneySample);
    var first = m.search(/[0-9]/);
    var last = m.length - 1 - String(m).split('').reverse().join('').search(/[0-9]/);
    this._money = {
      pre: first > 0 ? m.slice(0, first) : '',
      post: last < m.length - 1 ? m.slice(last + 1) : '',
      comma: /1[.,]00/.test(m) ? m.charAt(m.search(/1[.,]00/) + 1) : '.'
    };
    return this._money;
  };

  DeckboxDesigner.prototype.formatCents = function (cents) {
    var mp = this.moneyParts();
    var whole = Math.floor(cents / 100);
    var frac = String(Math.round(cents % 100));
    while (frac.length < 2) frac = '0' + frac;
    var s = String(whole).replace(/\B(?=(\d{3})+(?!\d))/g, mp.comma === ',' ? '.' : ',');
    return mp.pre + s + mp.comma + frac + mp.post;
  };

  DeckboxDesigner.prototype.currentVariant = function () {
    var names = this.optionNames || [];
    var groups = this.readRealOptions();
    var sel = {};
    groups.forEach(function (g) { if (g.selected != null) sel[g.name] = g.selected; });
    for (var i = 0; i < this.variants.length; i++) {
      var v = this.variants[i], ok = true;
      for (var j = 0; j < names.length; j++) {
        if (sel[names[j]] !== undefined && v.o[j] !== sel[names[j]]) { ok = false; break; }
      }
      if (ok) return v;
    }
    return null;
  };

  DeckboxDesigner.prototype.selectedQty = function () {
    var groups = this.readRealOptions();
    for (var i = 0; i < groups.length; i++) {
      if (this.isQtyOption(groups[i].name) && groups[i].selected) {
        var m = /(\d[\d,\s]*)/.exec(groups[i].selected);
        if (m) return parseInt(m[1].replace(/[,\s]/g, ''), 10);
      }
    }
    return null;
  };

  DeckboxDesigner.prototype.updatePrice = function () {
    if (!this.el.priceNow) return;
    var v = this.currentVariant();
    if (!v) { this.el.priceNow.textContent = ''; return; }

    this.el.priceNow.textContent = v.p;

    if (this.el.priceWas) {
      var show = v.cp && v.cp > v.c;
      this.el.priceWas.textContent = show ? this.formatCents(v.cp) : '';
      this.el.priceWas.hidden = !show;
    }

    if (this.el.priceUnit) {
      var q = this.selectedQty();
      if (q && q > 1) {
        this.el.priceUnit.textContent = this.formatCents(v.c / q) + ' each';
        this.el.priceUnit.hidden = false;
      } else {
        this.el.priceUnit.hidden = true;
      }
    }
  };

  // ---- add to cart -------------------------------------------------------

  /**
   * Associates our button with the real product form via the form="" attribute,
   * so it submits through product-form.js exactly like Metro's own button --
   * including the AJAX cart drawer and our own artwork gate.
   */
  DeckboxDesigner.prototype.wireAtc = function () {
    var btn = this.el.atc;
    if (!btn) return;
    var form = this.findForm();
    if (!form) return;

    // NOT form.id -- every Shopify product form contains <input name="id"> for
    // the variant, and HTMLFormElement's legacy named-property getter exposes
    // that input as an OWN property, shadowing the inherited `id` accessor.
    // form.id therefore returns an HTMLInputElement, and setting it does
    // nothing useful. Always go through get/setAttribute on a product form.
    var formId = form.getAttribute('id');
    if (!formId) {
      formId = 'DeckboxDesignerProductForm';
      form.setAttribute('id', formId);
    }
    btn.setAttribute('form', formId);

    if (btn.__dbxWired) return;
    btn.__dbxWired = true;
    var self = this;
    btn.addEventListener('click', function () {
      if (btn.getAttribute('aria-disabled') === 'true') return;
      btn.classList.add('dbx-loading');
      setTimeout(function () { btn.classList.remove('dbx-loading'); }, 5000);
    });
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      try {
        subscribe(PUB_SUB_EVENTS.cartUpdate, function () { btn.classList.remove('dbx-loading'); });
        subscribe(PUB_SUB_EVENTS.cartError, function () { btn.classList.remove('dbx-loading'); });
      } catch (e) { /* not fatal */ }
    }
  };

  /**
   * Relocates Metro's own buy-buttons block into the tool.
   *
   * Unlike the variant picker, this CAN be moved. Every `this.productForm`
   * reference in product-info.js is null-safe (`?.` or `?? document`), and the
   * variant re-render finds `#ProductBuyButtons-…` by id across the whole
   * document, so it keeps updating in its new home. The containment guard only
   * governs option changes, not form submission.
   */
  DeckboxDesigner.prototype.adoptBuyButtons = function () {
    if (!this.adoptBuy) return;
    var slot = this.el.buySlot;
    if (!slot) return;
    var scope = this.productScope();
    var bb = (scope.querySelector && scope.querySelector('[id^="ProductBuyButtons-"]')) ||
             document.querySelector('[id^="ProductBuyButtons-"]');
    if (!bb || slot.contains(bb)) return;
    slot.appendChild(bb);
    this.root.classList.add('dbx--adopted-buy');
  };

  /**
   * Turns a run of the product description into one of Metro's collapsible rows.
   *
   * The description is product data, shared with the LIVE theme, so it is NOT
   * edited: the nodes are relocated in the DOM on this template only. Editing
   * the description in admin would strip the content from the live product page
   * too, since Metro is still a draft. This way the source stays intact and the
   * row updates automatically if the description is ever changed.
   */
  DeckboxDesigner.prototype.collapseDescriptionSection = function () {
    var headingText = (this.collapseHeading || '').trim();
    if (!headingText) return;

    var desc = document.querySelector('[product-description], .product-block-desc');
    if (!desc || desc.__dbxCollapsed) return;

    var needle = headingText.toLowerCase().replace(/[:\s]+$/, '');
    var candidates = desc.querySelectorAll('p, h2, h3, h4, h5, h6');
    var head = null;
    for (var i = 0; i < candidates.length; i++) {
      var txt = (candidates[i].textContent || '').trim().toLowerCase().replace(/[:\s]+$/, '');
      if (txt === needle) { head = candidates[i]; break; }
    }
    if (!head) return;

    // everything up to the next paragraph or heading belongs to this section
    var body = [];
    var n = head.nextElementSibling;
    while (n && !/^(P|H[1-6])$/.test(n.tagName)) {
      body.push(n);
      n = n.nextElementSibling;
    }
    if (!body.length) return;

    var wrap = document.createElement('div');
    wrap.className = 'product-block product-collapsible dbx-collapsed-section';

    var details;
    try {
      // upgrades to Metro's accordion behaviour where customized built-ins are
      // supported; falls back to a plain <details>, which still opens and closes
      details = document.createElement('details', { is: 'accordion-details' });
    } catch (e) {
      details = document.createElement('details');
    }
    details.className = 'collapsible-product-content-item';
    if (this.collapseOpen) details.setAttribute('open', '');

    var summary = document.createElement('summary');
    summary.className = 'collapsible-heading';
    var label = document.createElement('div');
    label.className = 'collapsible-heading-text text font-bold';
    label.textContent = headingText.replace(/[:\s]+$/, '');
    summary.appendChild(label);

    var icon = document.querySelector('.product-collapsible .collapsible-icon');
    if (icon) {
      summary.appendChild(icon.cloneNode(true));
    } else {
      var span = document.createElement('span');
      span.className = 'collapsible-icon';
      span.textContent = '+';
      summary.appendChild(span);
    }
    details.appendChild(summary);

    var content = document.createElement('div');
    content.className = 'collapsible-content';
    content.setAttribute('collapsible-content-wrapper', '');
    var inner = document.createElement('div');
    inner.className = 'collapsible-content-body rte';
    for (var j = 0; j < body.length; j++) inner.appendChild(body[j]);
    content.appendChild(inner);
    details.appendChild(content);
    wrap.appendChild(details);

    head.parentNode.insertBefore(wrap, head);
    head.parentNode.removeChild(head);
    desc.__dbxCollapsed = true;
  };

  /**
   * Moves the Shop Pay installments banner in beneath the express checkout
   * button and above "More payment options".
   *
   * Shopify renders the payment button and its more-options link
   * asynchronously, so this watches for them rather than racing them. Placement
   * is INSIDE .shopify-payment-button, which is Shopify's own component -- if it
   * ever re-renders and drops our node, the poll re-places it.
   */
  DeckboxDesigner.prototype.adoptPaymentTerms = function () {
    if (!this.movePaymentTerms) return;
    var slot = this.el.buySlot;
    if (!slot) return;

    // Hold the node across re-renders: Metro replaces ProductBuyButtons'
    // innerHTML on every variant change, which detaches anything placed inside
    // it. The reference survives, so it can simply be re-inserted.
    var terms = this._paymentTerms ||
                document.querySelector('.product-block-payment-installments');
    if (!terms) return;
    this._paymentTerms = terms;

    if (this._termsPoll) { clearInterval(this._termsPoll); this._termsPoll = null; }

    // Move it in IMMEDIATELY. Position is refined below once Shopify's express
    // button renders, but the banner must never be left sitting in the product
    // column waiting for a button that may never appear -- it renders lazily,
    // and stays hidden until artwork exists.
    if (!slot.contains(terms)) slot.appendChild(terms);

    function refine() {
      var payBtn = slot.querySelector('.shopify-payment-button');
      if (!payBtn) return false;
      var more = payBtn.querySelector('.shopify-payment-button__more-options');
      if (more) {
        // the requested spot: under Buy with Shop, above More payment options
        if (terms.parentNode !== payBtn || terms.nextElementSibling !== more) {
          payBtn.insertBefore(terms, more);
        }
        return true;
      }
      // express button present but no more-options link: sit just beneath it
      if (terms.previousElementSibling !== payBtn) {
        payBtn.parentNode.insertBefore(terms, payBtn.nextSibling);
      }
      return false;
    }

    if (refine()) return;
    var self = this;
    var tries = 0;
    this._termsPoll = setInterval(function () {
      tries += 1;
      if (refine() || tries > 40) {                   // ~10s
        clearInterval(self._termsPoll);
        self._termsPoll = null;
      }
    }, 250);
  };

  /**
   * Catches any buy attempt made before a design exists.
   *
   * Bound on .dbx-buy, not on the buttons: the CSS overlay covers the whole buy
   * area, so this receives the click no matter which control was aimed at --
   * including Shopify's express checkout, which may live in an iframe that a
   * button-level listener could never observe.
   */
  DeckboxDesigner.prototype.bindBuyGuard = function () {
    var buy = this.root.querySelector('.dbx-buy');
    if (!buy || buy.__dbxBuyGuard) return;
    buy.__dbxBuyGuard = true;
    var self = this;

    buy.addEventListener('click', function (e) {
      if (!self.requireArtwork || self.state.file) return;   // all good, let it through
      e.preventDefault();
      e.stopPropagation();

      self.say('Please upload your design first — both Add to cart and express checkout need it.', true);

      var zone = self.el.dropzone;
      if (zone) {
        zone.classList.remove('dbx-flash');
        void zone.offsetWidth;                                // restart the animation
        zone.classList.add('dbx-flash');
        setTimeout(function () { zone.classList.remove('dbx-flash'); }, 1200);
      }
      var target = self.el.canvasWrap || zone || self.el.view;
      if (target && target.scrollIntoView) {
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, true);
  };

  /**
   * Pins the price to the Add-to-cart button's actual vertical position.
   *
   * Metro's adopted buy-buttons block renders extra elements and margins ABOVE
   * the buttons (error wrapper, form chrome), so the buttons sit lower than the
   * grid row top while the price stays at it -- a gap no static CSS can predict.
   * Measure where the button really is and offset the price to match.
   */
  DeckboxDesigner.prototype.alignPriceToButtons = function () {
    var price = this.root.querySelector('.dbx-price');
    var slot = this.el.buySlot;
    if (!price) return;
    var btn = (slot && slot.querySelector(
      '.addtocart-button, .shopify-payment-button__button, [name="add"]'));
    if (!btn || !slot) { price.style.marginTop = ''; return; }
    var offset = Math.round(btn.getBoundingClientRect().top -
                            slot.getBoundingClientRect().top);
    price.style.marginTop = (offset > 0 && offset < 200) ? offset + 'px' : '';
  };

  // ----------------------------------------------------------- cart handoff

  DeckboxDesigner.prototype.designNote = function () {
    var s = this.size(), st = this.state;
    var deg = Math.round(st.rot * 180 / Math.PI);
    var parts = [];

    // Shopify replaces the uploaded filename with a hash, so the customer's own
    // name for the file is recorded here. If a file is ever missing from an
    // order -- express checkout is the likely case -- this is what lets you ask
    // for the right thing by name instead of "please resend your artwork".
    if (st.file && st.file.name) parts.push(st.file.name);

    parts = parts.concat([
      'Mach 3 wrap ' + s.w + 'x' + s.h + 'mm',
      st.colorKey + ' leather',
      'scale ' + st.scale.toFixed(2),
      'offset ' + st.tx.toFixed(1) + 'mm,' + st.ty.toFixed(1) + 'mm',
      'rotate ' + deg + 'deg'
    ]);
    if (this.lastCover) {
      parts.push('src ' + Math.round((MM_PER_INCH * PX_PER_MM) / this.lastCover) + 'ppi');
    }
    return parts.join(' | ');
  };

  DeckboxDesigner.prototype.setFileOnInput = function (input, file) {
    if (!input) return;
    if (!file) { input.value = ''; return; }
    try {
      var dt = new DataTransfer();
      dt.items.add(file);
      input.files = dt.files;
    } catch (e) {
      console.warn('[deckbox-designer] could not attach file to form input', e);
    }
  };

  /**
   * Injects the line-item-property inputs into the product form.
   * Must be re-run after every variant change: product-info.js replaces the
   * innerHTML of the buy-buttons container, which destroys these inputs.
   */
  DeckboxDesigner.prototype.ensureFormInputs = function () {
    var form = this.findForm();
    if (!form) return;
    this.form = form;

    var self = this;
    function ensure(key, type, name) {
      var found = form.querySelector('[data-dbx-prop="' + key + '"]');
      if (!found) {
        found = document.createElement('input');
        found.type = type;
        found.name = name;
        found.setAttribute('data-dbx-prop', key);
        found.className = 'dbx-visually-hidden';
        if (type === 'file') found.setAttribute('tabindex', '-1');
        form.appendChild(found);
      }
      return found;
    }

    var art = ensure('art', 'file', 'properties[Artwork]');
    var shot = ensure('shot', 'file', 'properties[Design Preview]');
    var note = ensure('note', 'hidden', 'properties[_design]');

    // Design Preview is BACK (2026-09-29, merchant request). It was removed
    // 2026-08-22 when it carried a 3D screenshot; what ships now is different:
    // captureShot() renders the flat print sheet with its die/trim/crease
    // guides at print resolution, which is what the print team checks
    // placement against. Two files per line: this preview + the original art.
    this.setFileOnInput(art, this.state.file);
    this.setFileOnInput(shot, this.shotFile);
    note.value = this.state.file ? this.designNote() : '';

    if (!form.__dbxGuard) {
      form.__dbxGuard = true;
      form.addEventListener('submit', function (e) {
        if (!self.requireArtwork) return;

        if (!self.state.file) {
          e.preventDefault();
          e.stopImmediatePropagation();
          self.say('Please upload your design before adding to cart.', true);
          self.root.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return;
        }

      }, true);
    }
  };

  DeckboxDesigner.prototype.updateGate = function () {
    if (!this.requireArtwork) return;
    var form = this.form || this.findForm();
    if (!form) return;
    var ready = !!this.state.file;
    // Includes the sticky button, which sits outside the form and is associated
    // via its form="" attribute. HTMLButtonElement.form resolves that for us.
    var all = document.querySelectorAll('[name="add"]');
    var btns = [];
    for (var n = 0; n < all.length; n++) {
      if (all[n].form === form) btns.push(all[n]);
    }
    for (var i = 0; i < btns.length; i++) {
      var b = btns[i];
      if (ready) {
        if (b.getAttribute('data-dbx-blocked') === 'true') {
          b.removeAttribute('data-dbx-blocked');
          b.removeAttribute('aria-disabled');
          b.classList.remove('dbx-blocked');
        }
      } else {
        b.setAttribute('data-dbx-blocked', 'true');
        b.setAttribute('aria-disabled', 'true');
        b.classList.add('dbx-blocked');
      }
    }
    this.root.classList.toggle('dbx--ready', ready);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', boot);
})();
